import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { ArrowRight, Heart, Target, Lightbulb, Users } from "lucide-react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const StorySection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;
  max-width: 1024px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StoryContent = styled.div``;

const StoryTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2rem);
  font-weight: 700;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const StoryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StoryParagraph = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const StatCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  text-align: center;
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const StatValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2rem);
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const ValuesSection = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const SectionHeader = styled.div`
  max-width: 768px;
  margin: 0 auto 64px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2.25rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const SectionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 1280px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ValueCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  text-align: center;
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ValueIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;
`;

const ValueTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const ValueDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const TeamSection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 1024px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TeamCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  text-align: center;
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const TeamAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: ${({ theme }) => theme.typography.fontSize.h3};
  font-weight: 700;
  color: white;
`;

const TeamName = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const TeamRole = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const CTASection = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
  }
`;

const values = [
  {
    icon: Heart,
    title: "Customer First",
    description: "Everything we build starts with understanding and solving real problems for our users.",
  },
  {
    icon: Target,
    title: "Excellence",
    description: "We hold ourselves to the highest standards in everything we do, from code to customer service.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We're constantly pushing boundaries and exploring new ways to make work better.",
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "Great things happen when talented people work together towards a shared vision.",
  },
];

const team = [
  { name: "Alex Rivera", role: "CEO & Co-founder", avatar: "AR" },
  { name: "Jordan Kim", role: "CTO & Co-founder", avatar: "JK" },
  { name: "Sam Patel", role: "Head of Product", avatar: "SP" },
  { name: "Taylor Chen", role: "Head of Design", avatar: "TC" },
  { name: "Morgan Lee", role: "Head of Engineering", avatar: "ML" },
  { name: "Casey Brown", role: "Head of Sales", avatar: "CB" },
];

const stats = [
  { label: "Founded", value: "2021" },
  { label: "Team Members", value: "120+" },
  { label: "Countries", value: "40+" },
  { label: "Customers", value: "50K+" },
];

export default function About() {
  return (
    <Layout>
      <SEO
        title="About Us"
        description="Learn about Kliq CRM's mission to empower teams everywhere with intelligent tools that make work more productive, creative, and enjoyable."
        keywords="about, company, team, mission, values"
        hreflang
      />
      <PageTransition>
        {/* Hero */}
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">About Us</Badge>
              </BadgeWrapper>
              <Title>
                Building the future of <GradientText>work</GradientText>
              </Title>
              <Description>
                We're on a mission to empower teams everywhere with intelligent tools that make work more productive, creative, and enjoyable.
              </Description>
            </HeroContent>
          </Container>
        </Section>

        {/* Story */}
        <StorySection>
          <Container>
            <StoryGrid>
              <StoryContent>
                <StoryTitle>Our Story</StoryTitle>
                <StoryText>
                  <StoryParagraph>
                    Kliq CRM was born from a simple observation: teams everywhere were drowning in tools that were supposed to make them more productive, but instead added complexity and friction to their work.
                  </StoryParagraph>
                  <StoryParagraph>
                    Founded in 2021 by a team of engineers and designers who had experienced this firsthand, we set out to build something different—a platform that truly understands how modern teams work and adapts to their needs.
                  </StoryParagraph>
                  <StoryParagraph>
                    Today, Kliq CRM powers over 50,000 teams across 120 countries, helping them ship faster, collaborate better, and focus on what matters most.
                  </StoryParagraph>
                </StoryText>
              </StoryContent>
              <StatsGrid>
                {stats.map((stat) => (
                  <StatCard key={stat.label}>
                    <StatValue>{stat.value}</StatValue>
                    <StatLabel>{stat.label}</StatLabel>
                  </StatCard>
                ))}
              </StatsGrid>
            </StoryGrid>
          </Container>
        </StorySection>

        {/* Values */}
        <ValuesSection>
          <Container>
            <SectionHeader>
              <SectionTitle>Our Values</SectionTitle>
              <SectionDescription>The principles that guide everything we do.</SectionDescription>
            </SectionHeader>

            <ValuesGrid>
              {values.map((value) => (
                <ValueCard key={value.title}>
                  <ValueIconBox>
                    <value.icon size={24} />
                  </ValueIconBox>
                  <ValueTitle>{value.title}</ValueTitle>
                  <ValueDescription>{value.description}</ValueDescription>
                </ValueCard>
              ))}
            </ValuesGrid>
          </Container>
        </ValuesSection>

        {/* Team */}
        <TeamSection>
          <Container>
            <SectionHeader>
              <SectionTitle>Meet the Team</SectionTitle>
              <SectionDescription>The passionate people behind Kliq CRM.</SectionDescription>
            </SectionHeader>

            <TeamGrid>
              {team.map((member) => (
                <TeamCard key={member.name}>
                  <TeamAvatar>{member.avatar}</TeamAvatar>
                  <TeamName>{member.name}</TeamName>
                  <TeamRole>{member.role}</TeamRole>
                </TeamCard>
              ))}
            </TeamGrid>
          </Container>
        </TeamSection>

        {/* CTA */}
        <CTASection>
          <Container>
            <HeroContent>
              <SectionTitle>Join our journey</SectionTitle>
              <Description style={{ marginBottom: "32px" }}>
                We're always looking for talented people who share our vision. Check out our open positions.
              </Description>
              <CTAWrapper>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="hero" size="lg">
                    View Open Positions
                    <ArrowRight size={20} />
                  </Button>
                </Link>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="outline" size="lg">
                    Contact Us
                  </Button>
                </Link>
              </CTAWrapper>
            </HeroContent>
          </Container>
        </CTASection>
      </PageTransition>
    </Layout>
  );
}
